import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToWindowEdges } from '@dnd-kit/modifiers';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Add, Delete } from '@mui/icons-material';
import { Box, Grid, Typography } from '@mui/material';

import { useState, useEffect } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import { PrimaryButton, IconButton, SortableItem } from '../default';
import { ScoreRangeRulesModal } from '../modals/ScoreRangeRulesModal';
import { FormInputScore } from './formInputs';

/**
 * Renders a form component for credit policy score ranges.
 * 
 * @author Maria Eduarda Franke
 * @returns {JSX.Element} - The rendered form component.
 */
export const ScoreRangeForm = () => {

    const { control, watch, trigger, formState: { errors } } = useFormContext()

    const { fields, move, append, remove, update } = useFieldArray({
        control,
        name: 'scoreRanges'
    })

    const watchedFields = watch('scoreRanges')

    const [dragEnd, setDragEnd] = useState(false)
    const [openModal, setOpenModal] = useState(false)
    const [countRanges, setCountRanges] = useState(fields.length)
    const [selectedRange, setSelectedRange] = useState(null)
    const [selectedIndex, setSelectedIndex] = useState(null)

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: (sortableKeyboardCoordinates) => sortableKeyboardCoordinates
        })
    )

    /** Handle Drag and Drop Changes */
    const handleDragEnd = (event) => {
        const { active, over } = event

        if (over && active.id !== over?.id) {
            const activeIndex = active.data.current?.sortable?.index
            const overIndex = over.data.current?.sortable?.index

            if (activeIndex !== undefined && overIndex !== undefined) {
                move(activeIndex, overIndex)
            }

            setDragEnd(true)
        }
    }

    useEffect(() => {
        if (dragEnd) {
            updateScores()
        }
    }, [fields])

    const updateScores = () => {

        watchedFields.forEach((field, index) => {

            const minScore = index === 0 ? 0 : parseInt(watchedFields[index - 1].maxScore, 10) + 1

            update(index, { ...field, minScore: minScore })
            trigger(`scoreRanges.${index}.maxScore`)
        })

        setDragEnd(false)
    }

    /** Handle Max Score Input Changes */
    useEffect(() => {
        const subscription = watch((value, { name, type }) => {
            if (type === 'change' && name.includes('maxScore')) {

                const index = parseInt(name.split('.')[1], 10)
                let maxScore = parseInt(value.scoreRanges[index].maxScore, 10)
                trigger(`scoreRanges.${index}.maxScore`)

                if (index < fields.length - 1 && !isNaN(maxScore)) {

                    const nextMinScore = maxScore + 1
                    const nextMaxScore = parseInt(watch(`scoreRanges.${index + 1}.maxScore`), 10)
                    const nextScoreRangeId = watch(`scoreRanges.${index + 1}.scoreRangeId`)
                    const nextConsumptionRanges = watch(`scoreRanges.${index + 1}.consumptionRanges`)

                    update(index + 1, { minScore: nextMinScore, maxScore: nextMaxScore, scoreRangeId: nextScoreRangeId, consumptionRanges: nextConsumptionRanges })
                    trigger(`scoreRanges.${index + 1}.maxScore`)
                }

            }
        })
        return () => subscription.unsubscribe()

    }, [watch, fields.length, trigger, update])

    /** Handle Score Ranges Changes: append and remove */
    const handleRange = async () => {
        const isValid = await trigger('scoreRanges')

        if (!isValid) {
            return
        }

        const lastMaxScore = parseInt(watch(`scoreRanges.${countRanges - 1}.maxScore`), 10)

        if (lastMaxScore == 1000) {
            return
        }

        if (!isNaN(lastMaxScore) && lastMaxScore < 1000) {
            appendRange(lastMaxScore)
        }
    }

    const appendRange = lastMaxScore => {
        append({ scoreRangeId: uuidv4(), minScore: lastMaxScore + 1, maxScore: '' })
        setCountRanges(prevCount => prevCount + 1)
    }

    const removeRange = index => {

        const minScoreToSet = watchedFields[index].minScore

        remove(index)
        setCountRanges(prevCount => prevCount - 1)

        if (index < watchedFields.length - 1) {
            update(index, { ...watchedFields[index + 1], minScore: minScoreToSet })
            trigger(`scoreRanges.${index}.maxScore`)
        }
    }

    /** Render Score Ranges Fields */
    const renderRanges = () => {

        return fields.map((field, index) => (

            <SortableItem key={field.id} id={field.id} fieldId={field.scoreRangeId}>
                <Box
                    display="flex"
                    alignItems="center"
                    padding={2}
                >
                    <Grid container spacing={2} flex={1}>
                        <Grid item xs={6}>
                            <FormInputScore
                                name={`scoreRanges.${index}.minScore`}
                                label="Score Mínimo"
                                control={control}
                                fullWidth
                                required
                                disabled
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <FormInputScore
                                name={`scoreRanges.${index}.maxScore`}
                                label="Score máximo"
                                placeholder="Escreva o valor máximo de score"
                                control={control}
                                errors={errors.scoreRanges?.[index]?.maxScore}
                                fullWidth
                                required
                            />
                        </Grid>
                    </Grid>

                    <Box display="flex" alignItems="center" gap={1} ml={2}>
                        <PrimaryButton
                            label={watchedFields?.[index]?.consumptionRanges?.length > 0 ? "Editar Regras" : "Definir Regras"}
                            onClick={() => {
                                setSelectedRange(watchedFields[index])
                                setSelectedIndex(index)
                                setOpenModal(true)
                            }}
                            sx={{ whiteSpace: 'nowrap', textTransform: 'uppercase', fontWeight: 'bold' }}
                            variant={watchedFields?.[index]?.consumptionRanges?.length > 0 ? "outlined" : "contained"}
                            disabled={!watchedFields[index].maxScore}
                        />

                        { index !== 0 && (
                            <IconButton
                                onClick={() => removeRange(index)}
                                disabled={index === 0}
                                size="small"
                            >
                                <Delete fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                </Box>
            </SortableItem>
        ))
    }

    /** Handle Score Range Rules Save */
    const handleRulesSave = (creditLimitMinValue, creditLimitMaxValue, intervalList) => {
        update(selectedIndex, {
            ...selectedRange,
            creditLimitMinValue,
            creditLimitMaxValue,
            consumptionRanges: intervalList
        })
    }

    const lastMaxScore = watchedFields.reduce(( previousMaxScore, curr) => {

        if (previousMaxScore === undefined) {
            return
        }

        const currentMaxScore = Number(curr.maxScore)

        if (!currentMaxScore) {
            return
        }

        if (currentMaxScore >= 1000) {
            return
        }

        const currentMinScore = Number(curr.minScore)
        if (currentMaxScore <= currentMinScore) {
            return
        }

        if (currentMaxScore <= previousMaxScore) {
            return
        }

        return currentMaxScore
    }, 0)

    return (
        <Box
            display='flex'
            flexDirection='column'
            alignItems='flex-end'
            width='100%'
            gap={2}
        >

            <Grid container alignItems="center" spacing={2}>
                <Grid item flexGrow={1}>

                    <Typography variant='h6' fontWeight='bold'>
                        Faixas de score
                    </Typography>
                    <Typography variant='body2'>
                        Defina as faixas de pontuação (0 a 1000) para aplicar regras de crédito específicas.
                    </Typography>
                </Grid>

                <Grid item xs='auto'>
                    <PrimaryButton
                        label='Adicionar Faixa'
                        disabled={!lastMaxScore}
                        Icon={Add}
                        onClick={async () => handleRange()}
                    />
                </Grid>
            </Grid>

            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToVerticalAxis, restrictToWindowEdges]}
            >
                <SortableContext items={fields} strategy={verticalListSortingStrategy}>
                    {renderRanges()}
                </SortableContext>
            </DndContext>

            <ScoreRangeRulesModal
                open={openModal}
                onClose={() => setOpenModal(false)}
                onSave={handleRulesSave}
                scoreRange={selectedRange}
                scoreRangeIndex={selectedIndex ?? 0}
            />

        </Box>
    )
}

ScoreRangeForm.propTypes = {}
